import { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Box, Typography } from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import { useHistory } from "react-router-dom";
import { COLORS } from "components/v3/Theme/colors";
import { PATHS } from "constants/pathConstants";
import PageTitle from "utils/PageTitle";
import {
  selectUuidsOfKitsJustOrderedToBeShownOnConclusionScreen,
  setUuidsOfKitsJustOrderedToBeShownOnConclusionScreen,
  selectKitsSelectedToBeOrdered,
  selectTestKitsGLIsCurrentlyOffering,
  selectHasPartialOrderFailure
} from "store/reducers/order/OrderInformationSlice";
import StepperComponent from "components/v3/pages/orderKit/OrderKitStepper";
import SideMenuLayout from "components/v3/pages/orderKit/SideMenuLayout";
import { testNameImageMap } from "components/v3/sharedComponents/SuggestedScreeningCard";
import GLButton from "components/v3/sharedComponents/GLButton";

const OrderKitConclusionPage = (props) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const kitsJustOrdered = useSelector(
    selectUuidsOfKitsJustOrderedToBeShownOnConclusionScreen
  );
  const numKitsJustOrdered = kitsJustOrdered?.length;
  const orderedMultipleKits = numKitsJustOrdered > 1;
  const testKitsGLIsCurrentlyOffering = useSelector(
    selectTestKitsGLIsCurrentlyOffering
  );
  const kitsGLCurrentOffers = useSelector(selectTestKitsGLIsCurrentlyOffering);
  const selectedKitsUuids = useSelector(selectKitsSelectedToBeOrdered);
  const selectedKits = useMemo(() => {
    return kitsGLCurrentOffers.filter((offer) =>
      selectedKitsUuids?.includes(offer?.test_uuid)
    );
  }, [kitsGLCurrentOffers, selectedKitsUuids]);
  const numSelectedKits = selectedKits?.length;
  const [currentStepIndex] = useState(numSelectedKits + 3);
  const hasPartialOrderFailure = useSelector(selectHasPartialOrderFailure);

  const mapUuidsIntoFullTest = () => {
    if (numKitsJustOrdered > 0 && testKitsGLIsCurrentlyOffering.length > 0) {
      return kitsJustOrdered.map((uuid) => {
        return testKitsGLIsCurrentlyOffering.find((test) => {
          return test.test_uuid === uuid;
        });
      });
    }
    return [];
  };

  const mappedOrders = mapUuidsIntoFullTest();

  useEffect(() => {
    if (kitsJustOrdered.length < 1) {
      history.push(PATHS.LANDING_PAGE);
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kitsJustOrdered]);

  PageTitle("Order Confirmation");

  return (
    <SideMenuLayout
      loaded={true}
      onboardingStepper={
        <StepperComponent activeStepIndex={currentStepIndex} />
      }
    >
      <Box
        sx={{
          alignItems: {
            xs: "center",
            md: "flex-start"
          },
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          gap: {
            xs: 3,
            md: "60px"
          },
          marginTop: 0,
          paddingTop: {
            xs: "30px",
            md: 0
          },
          paddingX: {
            xs: "20px",
            md: 0
          }
        }}
      >
        <Box
          sx={{
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            backgroundColor: COLORS.WHITE,
            borderRadius: 8,
            gap: 4,
            marginTop: 0,
            padding: {
              xs: "32px 16px",
              md: 4
            },
            width: "100%"
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 4
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "15px",
                padding: "20px 30px 20px 0px"
              }}
            >
              <Typography
                sx={{
                  background: `linear-gradient(to right, ${COLORS.ACCENT3}, ${COLORS.PRIMARY})`,
                  typography: {
                    xs: "h3_strong_serif",
                    md: "body_strong_xl_serif"
                  },
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent"
                }}
              >
                What's Next?
              </Typography>
              <Typography variant="body_strong_xl">
                {hasPartialOrderFailure
                  ? "Part of your request was successful, but at least one request failed!"
                  : "We've successfully processed your request!"}
              </Typography>
              {hasPartialOrderFailure && (
                <Typography variant="body" sx={{ marginTop: "10px" }}>
                  please return to the dashboard and follow the prompts to try
                  again
                </Typography>
              )}
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: {
                  xs: 3,
                  md: "60px"
                }
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "15px"
                }}
              >
                <Typography variant="body_strong_lg_primary">Step 1</Typography>
                <Typography variant="body">
                  {orderedMultipleKits
                    ? `You will receive ${numKitsJustOrdered} sample collection kits in the mail from
                      one of our laboratory partners for your genetic test.`
                    : `You will receive ${numKitsJustOrdered} sample collection kit in the mail from
                      one of our laboratory partners for your genetic test.`}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", md: "row" },
                    gap: "15px"
                  }}
                >
                  {mappedOrders.map(
                    ({ lab: currentKitLab, test_name: currentKitName }) => {
                      const currentKitImage = testNameImageMap[currentKitName];

                      return (
                        <Box
                          sx={{
                            alignItems: "center",
                            background: COLORS.ACCENT1_LIGHTEST,
                            borderRadius: "10px",
                            display: "flex",
                            gap: "10px",
                            padding: "30px 30px 30px 20px"
                          }}
                        >
                          <Box
                            sx={{
                              alignItems: "flex-start",
                              display: "flex",
                              gap: "10px"
                            }}
                          >
                            <Box
                              component="img"
                              alt={currentKitName}
                              src={currentKitImage}
                              sx={{
                                height: "40px",
                                width: "40px"
                              }}
                            />
                            <Box
                              sx={{
                                alignItems: "flex-start",
                                display: "flex",
                                flexDirection: "column",
                                gap: "10px"
                              }}
                            >
                              <Typography variant="body_strong">
                                {currentKitName}
                              </Typography>
                              <Typography variant="body_xs_dark_gray">
                                <Box sx={{ display: "inline" }}>
                                  Our laboratory partner
                                </Box>{" "}
                                <Typography
                                  sx={{ display: "inline" }}
                                  variant="body_strong_xs_dark_gray"
                                >
                                  {currentKitLab},
                                </Typography>{" "}
                                <Box sx={{ display: "inline" }}>
                                  will perform your test
                                </Box>
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      );
                    }
                  )}
                </Box>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "15px"
                }}
              >
                <Typography variant="body_strong_lg_primary">Step 2</Typography>
                <Typography variant="body">
                  {orderedMultipleKits
                    ? `Once your sample collection kits arrive, follow the directions provided.`
                    : `Once your sample collection kit arrives, follow the directions provided.`}
                </Typography>
                <Box component="ol" sx={{ paddingLeft: "16px" }}>
                  <Box component="li">
                    <Typography variant="body">
                      It is critical to follow the instructions provided in the
                      sample collection kit.
                    </Typography>
                  </Box>
                  <Box component="li">
                    <Typography variant="body">
                      Send it back in the mail to the laboratory. If the steps
                      are not followed properly, you may need to do another one.
                    </Typography>
                  </Box>
                  <Box component="li">
                    <Typography variant="body">
                      Please return your sample kit promptly to the laboratory.
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "15px"
                }}
              >
                <Typography variant="body_strong_lg_primary">Step 3</Typography>
                <Box>
                  <Typography variant="body">
                    Once the laboratory receives your kit, it will take several
                    weeks to complete your test. You will be notified when your
                    results are ready.
                  </Typography>
                  <Typography sx={{ marginTop: 3 }} variant="body">
                    You may get emails from our partners, but it is not required
                    to set up additional accounts for this testing. The Genomic
                    Life portal gives you access to your test results and
                    follow-up materials.
                  </Typography>
                </Box>
                <Typography variant="body_secondary">
                  Until then, let us know if you have any questions! Please feel
                  free to email us at{" "}
                  <Box
                    component="a"
                    href="mailto:navigation@genomiclife.com"
                    sx={{
                      textDecoration: "underline",
                      typography: "body_secondary"
                    }}
                  >
                    navigation@genomiclife.com
                  </Box>
                  .
                </Typography>
              </Box>
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: { xs: "flex-start", md: "flex-end" },
              width: "100%"
            }}
          >
            <GLButton
              buttonType="primary"
              text="Back to my dashboard"
              onClick={() => {
                dispatch(
                  setUuidsOfKitsJustOrderedToBeShownOnConclusionScreen([])
                );
                history.push(PATHS.LANDING_PAGE);
              }}
              leftIcon={<HomeIcon />}
            />
          </Box>
        </Box>
      </Box>
    </SideMenuLayout>
  );
};

export default OrderKitConclusionPage;
